import { captureMessage } from "@sentry/browser";
import { Immutable } from "immer";
import React from "react";

import { reverse } from "@reactivated";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { OrderStatus } from "../../../constants";
import { isoWebPageURL } from "../../../models/nominals";
import { IUser } from "../../../models/user";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import * as signals from "../../../store/signals";
import { navigateToURL } from "../../../utils/urls";
import {
    PAYMENT_STATES_MAX_POLLS,
    PAYMENT_STATES_POLL_INTERVAL_MS,
} from "../constants";
import { defaults } from "../defaults";
import { IReduxState } from "../reducers.interfaces";
import { completeOrderPayment, refreshPaymentStates } from "../thunks";
import {
    PAYMENT_REJECT_MSGS,
    buildCheckoutError,
} from "../thunks/paymentStates";
import { IPaymentStatesRefreshResult } from "../thunks/refreshPaymentStates";
import { ICheckoutHTTPError } from "../types";

const handlePaymentFrameLoaded = (
    user: IUser | null,
    dispatch: ReturnType<typeof useAppDispatch>,
    checkoutState: IReduxState,
    onCheckoutError: (err: ICheckoutHTTPError) => void,
) => {
    dispatch(completeOrderPayment({ user }))
        .unwrap()
        .catch((err: ICheckoutHTTPError) => {
            // In case of an error, reload the page to display it.
            onCheckoutError(err);
            const window = getWindow();
            if (window) {
                window.scrollTo({
                    top: 0,
                });
            }
            // turning off page reload so we don't lose cc error info
            // window.location.reload();
            // TODO should this be conditional?
        });
};

type MessageContext = {
    user: IUser | null;
    checkoutState: IReduxState;
    onCheckoutError: (err: ICheckoutHTTPError) => void;
    hasPaymentMethodError: boolean;
};

export const PaymentChildFrameListener = (
    props: Immutable<{
        onCheckoutError: (err: ICheckoutHTTPError) => void;
        hasPaymentMethodError: boolean;
    }>,
): React.ReactNode => {
    const dispatch = useAppDispatch();
    const user = useAppSelector((state) => state.common.user);
    const checkoutState = useAppSelector((state) => state.checkout || defaults);

    const prevPaymentDeclinedRef = React.useRef<boolean | null>(null);
    // Stops polls navigating after the timeout commits to order-pending.
    const timedOutRef = React.useRef(false);
    const messageContextRef = React.useRef<MessageContext>({
        user,
        checkoutState,
        onCheckoutError: props.onCheckoutError,
        hasPaymentMethodError: props.hasPaymentMethodError,
    });
    const dispatchRef = React.useRef(dispatch);

    React.useEffect(() => {
        messageContextRef.current = {
            user,
            checkoutState,
            onCheckoutError: props.onCheckoutError,
            hasPaymentMethodError: props.hasPaymentMethodError,
        };
    }, [
        user,
        checkoutState,
        props.onCheckoutError,
        props.hasPaymentMethodError,
    ]);

    React.useEffect(() => {
        dispatchRef.current = dispatch;
    }, [dispatch]);

    // Route a refresh outcome shared by polling and the payment timeout.
    // Returns true when the outcome ends the wait (navigation already
    // happened inside the thunk, or the decline error was surfaced).
    const handleRefreshResult = (
        result: IPaymentStatesRefreshResult,
    ): boolean => {
        if (result.status === "complete") {
            return true;
        }
        if (result.status === "declined") {
            messageContextRef.current.onCheckoutError(
                buildCheckoutError(
                    "global",
                    PAYMENT_REJECT_MSGS[result.methodType],
                ),
            );
            return true;
        }
        return false;
    };

    const handlePaymentTimeout = async (): Promise<void> => {
        const { checkoutState: latestCheckoutState, hasPaymentMethodError } =
            messageContextRef.current;
        const snapshot = latestCheckoutState.data.payment_states;
        // if payment is pending and no error messages shown
        if (
            !snapshot ||
            snapshot.order_status !== OrderStatus.PENDING ||
            hasPaymentMethodError
        ) {
            return;
        }
        // The timeout owns the outcome from here; stop the poll loop.
        timedOutRef.current = true;
        // Re-check the server before declaring the order failed — the
        // redux snapshot is stale if the FRAME_LOADED message was lost.
        try {
            const result = await dispatchRef
                .current(refreshPaymentStates())
                .unwrap();
            if (handleRefreshResult(result)) {
                return;
            }
            captureMessage(
                "Checkout still Pending at payment timeout; redirecting to order-pending",
                "warning",
            );
        } catch {
            // Fall through to the redirect so the safety net can't hang.
            captureMessage(
                "Payment-state recheck failed at timeout; redirecting to order-pending (server state unknown)",
                "error",
            );
        }
        navigateToURL(isoWebPageURL.wrap(reverse("checkout:order-pending")));
    };

    // Handle message events from payment iframe
    React.useEffect(() => {
        const window = getWindow();
        if (!window) {
            return;
        }

        const messageHandler = (event: MessageEvent) => {
            // Message is sent from a hidden iframe used to submit Cybersource data
            // See tsi-common/src/tsicommon/templates/oscar/checkout/payment-redirect.html
            // Both legitimate senders are same-origin.
            if (event.origin !== window.location.origin) {
                return;
            }
            const {
                user: latestUser,
                checkoutState: latestCheckoutState,
                onCheckoutError,
            } = messageContextRef.current;
            const currentDispatch = dispatchRef.current;
            const messageType: string = event.data.type;
            switch (messageType) {
                case "TSI_CHECKOUT_PAYMENT_FRAME_LOADED":
                    handlePaymentFrameLoaded(
                        latestUser,
                        currentDispatch,
                        latestCheckoutState,
                        onCheckoutError,
                    );
                    break;
                case "TSI_CHECKOUT_PAYMENT_TIMEOUT":
                    void handlePaymentTimeout();
                    break;
            }
        };

        window.addEventListener("message", messageHandler, false);

        return () => {
            window.removeEventListener("message", messageHandler);
        };
    }, []);

    // Poll payment states while the order is Pending after payment
    // submission — the iframe's FRAME_LOADED message can be lost.
    const orderStatus = checkoutState.data.payment_states
        ? checkoutState.data.payment_states.order_status
        : null;
    React.useEffect(() => {
        const window = getWindow();
        if (
            !window ||
            orderStatus !== OrderStatus.PENDING ||
            props.hasPaymentMethodError
        ) {
            return;
        }
        // A new Pending cycle (e.g. a retry after a decline) gets a fresh
        // poll budget; any previous attempt's timeout no longer applies.
        timedOutRef.current = false;
        let cancelled = false;
        let timerID: number | undefined;
        let pollCount = 0;
        const tick = async () => {
            if (timedOutRef.current) {
                return;
            }
            pollCount++;
            try {
                const result = await dispatchRef
                    .current(refreshPaymentStates())
                    .unwrap();
                // On decline, the thunk's redux write has already ended
                // this effect; the error is still surfaced from this tick.
                if (handleRefreshResult(result)) {
                    return;
                }
            } catch {
                // Transient error checking payment states; keep polling.
            }
            if (
                !cancelled &&
                !timedOutRef.current &&
                pollCount < PAYMENT_STATES_MAX_POLLS
            ) {
                timerID = window.setTimeout(
                    () => void tick(),
                    PAYMENT_STATES_POLL_INTERVAL_MS,
                );
            }
        };
        timerID = window.setTimeout(
            () => void tick(),
            PAYMENT_STATES_POLL_INTERVAL_MS,
        );
        return () => {
            cancelled = true;
            if (timerID !== undefined) {
                window.clearTimeout(timerID);
            }
        };
    }, [orderStatus, props.hasPaymentMethodError]);

    // Handle payment decline detection
    React.useEffect(() => {
        const currDeclined =
            checkoutState.data.payment_states &&
            checkoutState.data.payment_states.order_status ===
                OrderStatus.PAYMENT_DECLINED;

        // Trigger onPaymentDecline, but not if we already did trigger it.
        if (currDeclined && !prevPaymentDeclinedRef.current) {
            signals.checkout.onPaymentDecline.emit();
        }

        prevPaymentDeclinedRef.current = currDeclined;
    }, [checkoutState.data.payment_states]);

    return null;
};
